import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import Select from '../store/ThemedSelect';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { useSchoolYear } from '../store/SchoolYearCalculator';
import ErrorModalMessage from '../store/ErrorMessage';
import {
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

// Theater for Young People event type; differs per env (prod=14, dev=16).
// Defaults to the prod value; override with REACT_APP_TYP_EVENT_TYPE_ID for local dev.
const TYP_EVENT_TYPE_ID = Number(process.env.REACT_APP_TYP_EVENT_TYPE_ID) || 14;

const TheaterForYoungPeople = () => {
  const createEmptyDateSchool = () => ({
    school_id: null,
    number_of_students: null,
    number_of_teachers: null,
    grade_id: null,
  });

  const createEmptyEventProgram = () => ({
    event_template_id: null,
    name: null,
    description: '',
    location_id: null,
    coordinator_id: null,
    event_type_id: TYP_EVENT_TYPE_ID,
  });

  const createEmptyEventDate = (eventId = null) => ({
    event_dates_id: null,
    event_id: eventId,
    date: null,
    start_time: null,
    end_time: null,
    total_seats: null,
    sold_out: false,
  });

  const [eventsPrograms, setEventsPrograms] = useState([]);
  const [currentEventProgram, setCurrentEventProgram] = useState(createEmptyEventProgram());
  const [currentEventDate, setCurrentEventDate] = useState(createEmptyEventDate());
  const [dateSchools, setDateSchools] = useState([createEmptyDateSchool()]);
  const [eventDates, setEventDates] = useState([]);
  const [datesSchools, setDatesSchools] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [openStates, setOpenStates] = useState({});
  const [showEventDateModal, setShowEventDateModal] = useState(false);
  const [showDeletionError, setShowDeletionError] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [locations, setLocations] = useState([]);
  const [schools, setSchools] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [grades, setGrades] = useState([]);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [schoolYearList, setSchoolYearList] = useState([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState();
  const { authState } = useAuth();
  const { currentYear, schoolYearListParent } = useSchoolYear();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authState) {
      navigate('/login');
    }
  }, [authState, navigate]);

  useEffect(() => {
    if (currentYear && schoolYearListParent.length) {
      setSelectedSchoolYear(currentYear);
      setSchoolYearList(schoolYearListParent);
    }
  }, [currentYear, schoolYearListParent]);

  useEffect(() => {
    if (selectedSchoolYear) {
      fetchEventsPrograms();
      fetchEventDates();
      fetchDatesSchool();
    }
  }, [selectedSchoolYear]);

  useEffect(() => {
    fetchLocations();
    fetchSchools();
    fetchDistricts();
    fetchGrades();
  }, []);

  const fetchEventsPrograms = async () => {
    try {
      const response = await fetch(`${backendURL}/api/events-programs/year/${selectedSchoolYear}`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      const filteredEvents = data.filter(event => event.event_type_id === TYP_EVENT_TYPE_ID);
      setEventsPrograms(filteredEvents);
    } catch {
      setErrorMessage('Unable to fetch Theater for Young People events.');
    }
  };

  const fetchDatesSchool = async () => {
    try {
      const response = await fetch(`${backendURL}/api/dates-schools/year/${selectedSchoolYear}`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      setDatesSchools(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch dates-schools links:', error.message);
    }
  };

  const fetchEventDates = async () => {
    try {
      const response = await fetch(`${backendURL}/api/event-dates/year/${selectedSchoolYear}`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      const sortedData = data.sort((a, b) => new Date(a.date) - new Date(b.date));
      setEventDates(sortedData);
    } catch (error) {
      console.error('Error fetching event dates:', error.message);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${backendURL}/api/locations`, { method: 'GET', credentials: 'include' });
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      console.error('Error fetching locations:', error.message);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await fetch(`${backendURL}/api/schools`, { method: 'GET', credentials: 'include' });
      const data = await response.json();
      setSchools(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching schools:', error.message);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await fetch(`${backendURL}/api/districts`, { method: 'GET', credentials: 'include' });
      const data = await response.json();
      setDistricts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching districts:', error.message);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await fetch(`${backendURL}/api/grades`, { method: 'GET', credentials: 'include' });
      const data = await response.json();
      setGrades(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching grades:', error.message);
    }
  };

  const schoolOptions = schools.map(school => {
    const district = districts.find(d => d.district_id === school.district_id);
    const districtName = district ? district.name : 'Unknown District';
    return { label: `${school.name} (${districtName})`, value: school.school_id };
  });

  const handleEventFormChange = (field, value) => {
    setCurrentEventProgram({
      ...currentEventProgram,
      [field]: value,
      school_year_id: parseInt(selectedSchoolYear, 10),
    });
  };

  const handleEventDateFormChange = (event) => {
    const { name, value } = event.target;
    setCurrentEventDate(prevState => ({
      ...prevState,
      [name]: value,
      school_year_id: parseInt(selectedSchoolYear, 10),
    }));
  };

  const handleDateSchoolFormChange = (index, field, value) => {
    const newDateSchools = [...dateSchools];
    newDateSchools[index][field] = value;
    setDateSchools(newDateSchools);
  };

  const addDateSchool = () => {
    setDateSchools([...dateSchools, createEmptyDateSchool()]);
  };

  const removeDateSchool = (index) => {
    const newDateSchools = [...dateSchools];
    newDateSchools.splice(index, 1);
    setDateSchools(newDateSchools.length ? newDateSchools : [createEmptyDateSchool()]);
  };

  const handleMarkForDeletion = (index) => {
    const itemToDelete = dateSchools[index];
    if (itemToDelete?.school_id) {
      setItemsToDelete([...itemsToDelete, itemToDelete]);
    }
    removeDateSchool(index);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    const method = currentEventProgram.event_id ? 'PUT' : 'POST';
    const url = currentEventProgram.event_id
      ? `${backendURL}/api/events-programs/${currentEventProgram.event_id}`
      : `${backendURL}/api/events-programs`;
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...currentEventProgram, school_year_id: parseInt(selectedSchoolYear, 10) }),
      credentials: 'include',
    });
    setCurrentEventProgram(createEmptyEventProgram());
    setShowForm(false);
    fetchEventsPrograms();
  };

  const handleNewEventDate = (eventProgramId) => {
    setCurrentEventDate(createEmptyEventDate(eventProgramId));
    setDateSchools([createEmptyDateSchool()]);
    setItemsToDelete([]);
    setShowEventDateModal(true);
  };

  const handleEventDateSubmit = async (e) => {
    e.preventDefault();
    if (!currentEventDate.date || !currentEventDate.start_time || !currentEventDate.end_time) {
      setErrorMessage('Please fill out the date, start time, and end time.');
      return;
    }
    try {
      const method = currentEventDate.event_dates_id ? 'PUT' : 'POST';
      const url = currentEventDate.event_dates_id
        ? `${backendURL}/api/event-dates/${currentEventDate.event_dates_id}`
        : `${backendURL}/api/event-dates`;
      const dateResponse = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(currentEventDate),
        credentials: 'include',
      });
      if (!dateResponse.ok) {
        throw new Error('Failed to save the event date.');
      }
      const savedDate = await dateResponse.json();

      if (savedDate && savedDate.event_dates_id) {
        const schoolRows = dateSchools.filter(ds => ds.school_id);
        const schoolPromises = schoolRows.map(ds => fetch(
          `${backendURL}/api/dates-schools/${savedDate.event_dates_id}/${ds.school_id}`,
          {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event_date_id: savedDate.event_dates_id,
              school_id: ds.school_id,
              number_of_students: ds.number_of_students === null || ds.number_of_students === ''
                ? null : parseInt(ds.number_of_students, 10),
              number_of_teachers: ds.number_of_teachers === null || ds.number_of_teachers === ''
                ? null : parseInt(ds.number_of_teachers, 10),
              grade_id: ds.grade_id ? parseInt(ds.grade_id, 10) : null,
              school_year_id: parseInt(selectedSchoolYear, 10),
            }),
            credentials: 'include',
          }
        ));
        await Promise.all(schoolPromises);

        const deletePromises = itemsToDelete
          .filter(item => item.school_id)
          .map(item => fetch(
            `${backendURL}/api/dates-schools/${savedDate.event_dates_id}/${item.school_id}`,
            { method: 'DELETE', credentials: 'include' }
          ));
        await Promise.all(deletePromises);
      }

      setCurrentEventDate(createEmptyEventDate());
      setDateSchools([createEmptyDateSchool()]);
      setItemsToDelete([]);
      setShowEventDateModal(false);
      fetchEventDates();
      fetchDatesSchool();
    } catch {
      setErrorMessage('Unable to save the event date with schools.');
    }
  };

  const editEventProgram = (eventProgram) => {
    setCurrentEventProgram(eventProgram);
    setShowForm(true);
  };

  const editEventDate = (eventDate) => {
    const linkedSchools = datesSchools.filter(ds => ds.event_date_id === eventDate.event_dates_id);
    setCurrentEventDate({ ...eventDate });
    setDateSchools(
      linkedSchools.length
        ? linkedSchools.map(ds => ({
            school_id: ds.school_id,
            number_of_students: ds.number_of_students,
            number_of_teachers: ds.number_of_teachers,
            grade_id: ds.grade_id ?? null,
          }))
        : [createEmptyDateSchool()]
    );
    setItemsToDelete([]);
    setShowEventDateModal(true);
  };

  const handleCancel = () => {
    setCurrentEventDate(createEmptyEventDate());
    setCurrentEventProgram(createEmptyEventProgram());
    setDateSchools([createEmptyDateSchool()]);
    setShowForm(false);
    setShowEventDateModal(false);
    setItemsToDelete([]);
  };

  const handleShowForm = () => {
    setCurrentEventProgram(createEmptyEventProgram());
    setShowForm(!showForm);
  };

  const deleteEventProgram = async (event_id) => {
    try {
      const response = await fetch(`${backendURL}/api/events-programs/${event_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        fetchEventsPrograms();
      } else {
        setShowDeletionError(true);
      }
    } catch (error) {
      setShowDeletionError(true);
      console.error('Please delete associated event dates before deleting an event.');
    }
  };

  const handleDeleteEventDate = () => {
    setConfirmationMessage('Are you sure you want to delete this event date?');
    setShowConfirmationModal(true);
  };

  const deleteEventDate = async (eventDateId) => {
    try {
      const deleteDateSchool = await fetch(`${backendURL}/api/dates-schools/${eventDateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (deleteDateSchool.ok) {
        const response = await fetch(`${backendURL}/api/event-dates/${eventDateId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (response.ok) {
          setShowEventDateModal(false);
          setShowConfirmationModal(false);
          fetchEventDates();
          fetchDatesSchool();
        } else {
          throw new Error('Failed to delete the event date');
        }
      }
    } catch (error) {
      console.error('Deletion failed:', error);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) {
      return 'no time set';
    }
    const [hours, minutes] = timeString.split(':');
    const hoursInt = parseInt(hours, 10);
    const suffix = hoursInt >= 12 ? 'PM' : 'AM';
    const formattedHours = ((hoursInt + 11) % 12 + 1);
    return `${formattedHours}:${minutes} ${suffix}`;
  };

  const getLocation = (locationId) => {
    const location = locations.find(loc => loc.location_id === locationId);
    return location ? location.name : 'No location set';
  };

  const getSchoolName = (schoolId) => {
    const schoolObj = schoolOptions.find(school => school.value === schoolId);
    return schoolObj ? schoolObj.label : 'School name failed to load';
  };

  const getProgramEventDates = (eventId) =>
    eventDates
      .filter(eventDate => eventDate.event_id === eventId)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

  const getDateSchoolLinksForDate = (eventDateId) =>
    datesSchools.filter(dateSchool => dateSchool.event_date_id === eventDateId);

  const getProgramSchoolLinks = (eventId) => {
    const dateIds = getProgramEventDates(eventId).map(d => d.event_dates_id);
    return datesSchools.filter(ds => dateIds.includes(ds.event_date_id));
  };

  const getProgramScheduleSummary = (eventId) => {
    const programDates = getProgramEventDates(eventId);
    const links = getProgramSchoolLinks(eventId);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming = programDates.find(d => d.date && new Date(d.date) >= today);
    const latest = programDates.length ? programDates[programDates.length - 1] : null;
    return {
      dateCount: programDates.length,
      nextDate: upcoming?.date || 'No upcoming dates',
      latestDate: latest?.date || 'No dates added',
      schoolCount: new Set(links.map(l => l.school_id)).size,
      studentTotal: links.reduce((total, l) => total + (parseInt(l.number_of_students, 10) || 0), 0),
      teacherTotal: links.reduce((total, l) => total + (parseInt(l.number_of_teachers, 10) || 0), 0),
    };
  };

  const getSchoolList = (eventId) => {
    const uniqueSchoolIds = [...new Set(getProgramSchoolLinks(eventId).map(l => l.school_id))];
    if (!uniqueSchoolIds.length) {
      return <p className="text-muted small mb-0">No schools linked yet.</p>;
    }
    return (
      <ul className="mb-0">
        {uniqueSchoolIds.map(schoolId => <li key={schoolId}>{getSchoolName(schoolId)}</li>)}
      </ul>
    );
  };

  const renderEventDates = (eventProgram) => {
    const programDates = getProgramEventDates(eventProgram.event_id);
    if (programDates.length === 0) {
      return (
        <div className="schedule-empty-state">
          No dates have been created for this event yet.
        </div>
      );
    }
    return (
      <div className="schedule-card-grid">
        {programDates.map(eventDate => {
          const linkedSchools = getDateSchoolLinksForDate(eventDate.event_dates_id);
          const totalStudents = linkedSchools.reduce(
            (total, link) => total + (parseInt(link.number_of_students, 10) || 0), 0
          );
          const totalTeachers = linkedSchools.reduce(
            (total, link) => total + (parseInt(link.number_of_teachers, 10) || 0), 0
          );
          const schoolPreview = linkedSchools
            .slice(0, 3)
            .map(link => getSchoolName(link.school_id))
            .join(', ');
          return (
            <button
              type="button"
              key={eventDate.event_dates_id}
              className="schedule-card"
              onClick={() => editEventDate(eventDate)}
            >
              <div className="schedule-card-header">
                <span className="schedule-card-title">{eventDate.date || 'No date set'}</span>
              </div>
              <div className="schedule-card-time">
                {`${formatTime(eventDate.start_time)} to ${formatTime(eventDate.end_time)}`}
              </div>
              <div className="schedule-card-meta">
                {`${linkedSchools.length} school${linkedSchools.length === 1 ? '' : 's'}, ${totalStudents} students, ${totalTeachers} teachers`}
              </div>
              <div className="schedule-card-preview">
                {schoolPreview || 'No school counts added yet'}
                {linkedSchools.length > 3 ? '…' : ''}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const toggleOpenState = (eventId) => {
    setOpenStates(prevOpenStates => ({
      ...prevOpenStates,
      [eventId]: !prevOpenStates[eventId],
    }));
  };

  const handleYearChange = (event) => {
    setSelectedSchoolYear(event.target.value);
  };

  const {
    items: visibleEventsPrograms,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(eventsPrograms, {
    searchAccessor: (eventProgram) =>
      [eventProgram.name, eventProgram.description].filter(Boolean).join(' '),
    sortOptions: [
      {
        key: 'name',
        label: 'Name',
        accessor: (eventProgram) => eventProgram.name?.toLowerCase() ?? '',
      },
      {
        key: 'location',
        label: 'Location',
        accessor: (eventProgram, ctx) =>
          ctx.getLocation(eventProgram.location_id)?.toLowerCase() ?? '',
      },
    ],
    context: { getLocation },
  });

  return (
    <div>
      <Button onClick={() => handleShowForm()} className="m-1">
        {showForm ? 'Show Events List' : 'Add New Event'}
      </Button>
      <div>
        <span className="fw-bold">Select School Year</span>
        <select value={selectedSchoolYear} onChange={handleYearChange}>
          <option value="" disabled>Select School Year</option>
          {schoolYearList.map((year) => (
            <option key={year.id} value={year.id}>{year.school_year}</option>
          ))}
        </select>
      </div>
      {showForm ? (
        <Form onSubmit={handleEventSubmit}>
          <Form.Group controlId="name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter event name"
              name="name"
              value={currentEventProgram.name || ''}
              onChange={e => handleEventFormChange('name', e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter event description"
              name="description"
              value={currentEventProgram.description || ''}
              onChange={e => handleEventFormChange('description', e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="location_id">
            <Form.Label>Location</Form.Label>
            <Form.Control
              as="select"
              name="location_id"
              value={currentEventProgram.location_id || ''}
              onChange={e => handleEventFormChange('location_id', e.target.value)}
            >
              <option value="">Select Location</option>
              {locations.map(location => (
                <option key={location.location_id} value={location.location_id}>{location.name}</option>
              ))}
            </Form.Control>
          </Form.Group>
          <Button type="submit" className="m-1">Submit</Button>
          <Button variant="secondary" className="m-1" onClick={handleCancel}>Cancel</Button>
        </Form>
      ) : (
        <>
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="event"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput
              label="Search Events"
              placeholder="Search by event name or description"
              {...search}
            />
            <SortControl {...sort} />
          </FilterBar>

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Theater for Young People Events</th>
              </tr>
            </thead>
            <tbody>
              {visibleEventsPrograms.length === 0 ? (
                <tr>
                  <td>
                    <div className="schedule-empty-state">
                      No events match the current filters.
                    </div>
                  </td>
                </tr>
              ) : visibleEventsPrograms.map(eventProgram => {
                const isOpen = openStates[eventProgram.event_id] || false;
                const summary = getProgramScheduleSummary(eventProgram.event_id);
                const rowDateSummary =
                  summary.dateCount === 0
                    ? 'No dates added'
                    : summary.nextDate !== 'No upcoming dates'
                      ? `Next: ${summary.nextDate}`
                      : `Most recent: ${summary.latestDate}`;

                return (
                  <React.Fragment key={eventProgram.event_id}>
                    <tr
                      className={`compact-row${isOpen ? ' is-open' : ''}`}
                      onClick={() => toggleOpenState(eventProgram.event_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td colSpan={1}>
                        <div className="event-program-row-content">
                          <div className="event-program-main">
                            <div className="event-program-name">{eventProgram.name || 'Untitled event'}</div>
                            <div className="event-program-type">Theater for Young People</div>
                            <div className="event-program-description">
                              {eventProgram.description || 'No description provided.'}
                            </div>
                          </div>

                          <div className="event-program-row-meta">
                            <span className="compact-pill">{getLocation(eventProgram.location_id)}</span>
                            <span className="compact-pill">{summary.dateCount} Date{summary.dateCount === 1 ? '' : 's'}</span>
                            <span className="compact-pill">{summary.schoolCount} School{summary.schoolCount === 1 ? '' : 's'}</span>
                            <span className="compact-pill">{summary.studentTotal} Students</span>
                          </div>

                          <div className="event-program-row-schedule">
                            <small>{rowDateSummary}</small>
                            <small>{`${summary.studentTotal} students, ${summary.teacherTotal} teachers`}</small>
                          </div>

                          <div className="event-program-row-flags">
                            <span className="event-access-badge private">Theater for Young People</span>
                            <span className="event-access-copy">
                              Admins enter per-school student counts, teacher counts, and grade for each date.
                            </span>
                          </div>

                          <div className="text-muted small event-program-expand">
                            {isOpen ? '▼ Collapse' : '▶ Expand'}
                          </div>
                        </div>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={1}>
                          <div className="event-program-detail-grid p-3 border rounded">
                            <div className="detail-panel">
                              <h6 className="mb-2">Overview</h6>
                              <div className="schedule-summary-row">
                                <strong>Location</strong>
                                <span>{getLocation(eventProgram.location_id)}</span>
                              </div>
                              <div className="mt-2 text-muted small">
                                {eventProgram.description || 'No description provided.'}
                              </div>
                            </div>

                            <div className="detail-panel">
                              <h6 className="mb-2">Schedule Summary</h6>
                              <div className="schedule-summary-row">
                                <strong>Dates</strong>
                                <span>{summary.dateCount}</span>
                              </div>
                              <div className="schedule-summary-row">
                                <strong>Next Date</strong>
                                <span>{summary.nextDate}</span>
                              </div>
                              <div className="schedule-summary-row">
                                <strong>Schools</strong>
                                <span>{summary.schoolCount}</span>
                              </div>
                              <div className="schedule-summary-row">
                                <strong>Tracked Attendance</strong>
                                <span>{`${summary.studentTotal} students, ${summary.teacherTotal} teachers`}</span>
                              </div>
                            </div>

                            <div className="detail-panel">
                              <h6 className="mb-2">Schools</h6>
                              {getSchoolList(eventProgram.event_id)}
                            </div>

                            <div className="detail-panel actions-panel">
                              <h6 className="mb-2">Actions</h6>
                              <Button type="button" onClick={() => editEventProgram(eventProgram)}>
                                <i className="bi bi-pencil"></i> Edit Event
                              </Button>
                              <Button type="button" onClick={() => handleNewEventDate(eventProgram.event_id)}>
                                New Date
                              </Button>
                              <Button type="button" variant="danger" onClick={() => deleteEventProgram(eventProgram.event_id)}>
                                <i className="bi bi-trash3"></i> Delete Event
                              </Button>
                            </div>

                            <div className="detail-panel event-program-dates-detail">
                              <div className="schedule-panel-toolbar">
                                <div>
                                  <div className="schedule-panel-title">Dates for {eventProgram.name}</div>
                                  <div className="schedule-panel-subtitle">
                                    Each date lists its attending schools with student/teacher counts and grade.
                                  </div>
                                </div>
                                <Button type="button" onClick={() => handleNewEventDate(eventProgram.event_id)}>
                                  New Date
                                </Button>
                              </div>
                              {renderEventDates(eventProgram)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </>
      )}

      <Modal size="lg" show={showEventDateModal} onHide={() => handleCancel()}>
        <Modal.Header>{currentEventDate.event_dates_id ? 'Edit Event Date' : 'Create New Event Date'}</Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Select Date</Form.Label>
              <Form.Control
                type="date"
                id="date"
                name="date"
                value={currentEventDate.date || ''}
                onChange={handleEventDateFormChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Start Time</Form.Label>
              <Form.Control
                type="time"
                id="start_time"
                name="start_time"
                value={currentEventDate.start_time || ''}
                onChange={handleEventDateFormChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>End Time</Form.Label>
              <Form.Control
                type="time"
                id="end_time"
                name="end_time"
                value={currentEventDate.end_time || ''}
                onChange={handleEventDateFormChange}
              />
            </Form.Group>

            <hr />
            <p className="mb-2 fw-bold">Attendance by School</p>
            {dateSchools.map((ds, index) => (
              <Row key={index} className="mb-3 align-items-end">
                <Col md={4}>
                  <Form.Group controlId={`school_${index}`}>
                    <Form.Label>School</Form.Label>
                    <Select
                      options={schoolOptions}
                      value={ds.school_id ? schoolOptions.find(s => s.value === ds.school_id) : null}
                      onChange={selectedOption => handleDateSchoolFormChange(index, 'school_id', selectedOption ? selectedOption.value : null)}
                      classNamePrefix="select"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group controlId={`number_of_students_${index}`}>
                    <Form.Label>Students</Form.Label>
                    <Form.Control
                      type="number"
                      value={ds.number_of_students ?? ''}
                      onChange={e => handleDateSchoolFormChange(index, 'number_of_students', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group controlId={`number_of_teachers_${index}`}>
                    <Form.Label>Teachers</Form.Label>
                    <Form.Control
                      type="number"
                      value={ds.number_of_teachers ?? ''}
                      onChange={e => handleDateSchoolFormChange(index, 'number_of_teachers', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId={`grade_${index}`}>
                    <Form.Label>Grade</Form.Label>
                    <Form.Control
                      as="select"
                      value={ds.grade_id ?? ''}
                      onChange={e => handleDateSchoolFormChange(index, 'grade_id', e.target.value)}
                    >
                      <option value="">Select Grade</option>
                      {grades.map(grade => (
                        <option key={grade.grade_id} value={grade.grade_id}>{grade.grade}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={1} className="text-end">
                  {index > 0 && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      title="Remove school"
                      onClick={() => handleMarkForDeletion(index)}
                    >
                      <i className="bi bi-trash3"></i>
                    </Button>
                  )}
                </Col>
              </Row>
            ))}
            <Button variant="primary" onClick={addDateSchool}>Add Another School</Button>
          </Form>
          <Modal.Footer className="modal-footer-buttons">
            {currentEventDate.event_dates_id ? (
              <Button variant="danger" onClick={() => handleDeleteEventDate()}>Delete</Button>
            ) : ''}
            <Button onClick={handleEventDateSubmit}>{currentEventDate.event_dates_id ? 'Update' : 'Create'}</Button>
            <Button variant="secondary" onClick={() => handleCancel()}>Cancel</Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

      <Modal show={showDeletionError} onHide={() => setShowDeletionError(false)}>
        <Modal.Header>Unable to process Request</Modal.Header>
        <Modal.Body>Please be sure to delete all associated dates with the event before deleting the event</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeletionError(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)}>
        <Modal.Header closeButton>Please Confirm!</Modal.Header>
        <Modal.Body>{confirmationMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => deleteEventDate(currentEventDate.event_dates_id)}>Delete</Button>
          <Button variant="secondary" onClick={() => setShowConfirmationModal(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      <ErrorModalMessage errorMessage={errorMessage} clearErrorMessage={() => setErrorMessage('')} />
    </div>
  );
};

export default TheaterForYoungPeople;
